import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Globe2, Cog, BrainCircuit, BarChart3, Target, ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { SERVICES, TONES, IMAGES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, SectionTitle, GoldButton } from "@/components/Section";

export const SERVICE_ICONS = { compass: Compass, globe: Globe2, cog: Cog, brain: BrainCircuit, chart: BarChart3, target: Target };

export const ServicesTeaser = () => {
  const track = useRef(null);
  const scrollBy = (dir) => track.current?.scrollBy({ left: dir * 380, behavior: "smooth" });

  return (
    <section className="relative py-24 lg:py-32 bg-[#F2EEE7] overflow-hidden" data-testid="services-teaser">
      <div className="absolute inset-x-0 top-0 hairline" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Eyebrow tone="#2C4A6F">Υπηρεσίες</Eyebrow>
            <SectionTitle className="mt-6">Έξι άξονες δουλειάς, <span className="italic text-[#2C4A6F]">ένας στόχος.</span></SectionTitle>
            <p className="mt-6 text-base leading-relaxed text-[#3B4A5E] max-w-xl">
              Κάθε άξονας μπορεί να λειτουργήσει μόνος του ή να συνδυαστεί σε ένα ενιαίο πλάνο για την επιχείρησή σας.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="services-scroll-prev" onClick={() => scrollBy(-1)} className="h-11 w-11 rounded-full border border-[#0E1B31]/15 bg-white/70 flex items-center justify-center text-[#0E1B31] hover:bg-[#0E1B31] hover:text-white transition-colors" aria-label="Προηγούμενο">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button data-testid="services-scroll-next" onClick={() => scrollBy(1)} className="h-11 w-11 rounded-full border border-[#0E1B31]/15 bg-white/70 flex items-center justify-center text-[#0E1B31] hover:bg-[#0E1B31] hover:text-white transition-colors" aria-label="Επόμενο">
              <ArrowRight className="h-4 w-4" />
            </button>
            <GoldButton to="/services" testId="services-teaser-link" variant="outline" className="ml-2 hidden sm:inline-flex">Όλες οι υπηρεσίες</GoldButton>
          </div>
        </Reveal>
      </div>

      <div ref={track} className="mt-14 flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory px-4 sm:px-6 lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))] pb-6">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.icon];
          const t = TONES[s.tone];
          const dark = s.tone === "navy";
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`snap-start shrink-0 w-[300px] sm:w-[360px] ${i % 2 === 1 ? "lg:mt-10" : ""}`}
            >
              <Link
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className={`group relative flex flex-col h-[440px] rounded-[30px] overflow-hidden border surface-hover ${dark ? "mesh-navy border-white/10 text-white" : "bg-white border-[#0E1B31]/[0.07]"}`}
              >
                <div className="relative h-[46%] overflow-hidden">
                  <img src={IMAGES[s.image]} alt="" className="h-full w-full object-cover transition-transform duration-1000 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 30%, ${dark ? "#0E1B31" : "#ffffff"} 100%)` }} />
                  <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-2xl glass text-[#0E1B31]" style={{ color: t.accent }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="absolute right-5 top-5 font-mono-brand text-xs text-white/90 glass-dark rounded-full px-2.5 py-1">{s.num}</span>
                </div>
                <div className="relative flex-1 flex flex-col px-7 pb-7 -mt-4">
                  <span className="inline-flex w-fit items-center rounded-full px-3 py-1 text-[10px] font-mono-brand tracking-[0.2em] uppercase" style={{ background: dark ? "rgba(255,255,255,0.1)" : t.tint, color: dark ? "#fff" : t.text }}>
                    {s.clusters[0].name}
                  </span>
                  <h3 className={`mt-4 font-display text-2xl font-semibold leading-snug ${dark ? "text-white" : "text-[#0E1B31]"}`}>{s.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed flex-1 ${dark ? "text-white/70" : "text-[#3B4A5E]"}`}>{s.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold tracking-wide" style={{ color: dark ? "#fff" : t.accent }}>
                    Αναλυτικά
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" style={{ background: t.accent }} />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
